import React, { createContext, useContext as useContextCore, useEffect } from 'react'
import { getWindow } from '@ztechapps/common.utils'
import { useSessionStorage } from 'react-use'

export type FloatingChatBubbleContextType = {
  /** Whether the chat modal is currently open */
  isOpen: boolean
  /** Toggle the modal open/closed state */
  toggleOpen: () => void
  /** Open the modal */
  open: () => void
  /** Close the modal */
  close: () => void
  /** Whether the chat modal is currently maximized */
  isMaximized: boolean
  /** Toggle the modal maximized/restored state */
  toggleMaximized: () => void
  /** Set the maximized state explicitly */
  setMaximized: (value: boolean) => void
}

const context = createContext({} as FloatingChatBubbleContextType)
const CoreProvider = context.Provider

export type FloatingChatBubbleProviderProps = {
  /** React children to render */
  children: React.ReactNode
  /** Whether the modal should be open by default. Default: false */
  defaultOpen?: boolean
  /** Storage key for persisting modal state. If not provided, state will not be persisted. */
  storageKey?: string
  /** Enable global keyboard shortcut (Ctrl/Cmd+K) to toggle the chat bubble. Default: true */
  enableKeyboardShortcut?: boolean
}

/**
 * Provider component for the floating chat bubble context.
 *
 * Manages the open/closed and maximized state of the chat modal with
 * sessionStorage persistence and optional keyboard shortcut support.
 *
 * sessionStorage (not localStorage) is intentional: the state must stay scoped
 * to the current tab. A new tab — e.g. the "View product" link opens one with
 * rel="noopener" — starts with a fresh sessionStorage, so the chat resets to
 * closed + restored instead of inheriting another window's open/maximized size.
 *
 * @example
 * ```tsx
 * <FloatingChatBubbleProvider defaultOpen={false} storageKey="ai-chat-state">
 *   <FloatingChatBubble />
 *   <ChatModal ... />
 * </FloatingChatBubbleProvider>
 * ```
 */
export const FloatingChatBubbleProvider = ({
  children,
  defaultOpen = false,
  storageKey = 'ai-chat-bubble-open',
  enableKeyboardShortcut = true,
}: FloatingChatBubbleProviderProps) => {
  // Per-tab open state — new tabs start fresh (see component doc above)
  const [isOpen, setIsOpen] = useSessionStorage(storageKey, defaultOpen)

  // Persist maximized state separately, derived from the same storageKey
  const maximizedStorageKey =
    storageKey === 'ai-chat-bubble-open' ? 'ai-chat-bubble-maximized' : `${storageKey}-maximized`
  const [isMaximized, setIsMaximized] = useSessionStorage(maximizedStorageKey, false)

  // Global keyboard shortcut: Ctrl/Cmd+K to toggle chat
  useEffect(() => {
    if (!enableKeyboardShortcut) return

    const win = getWindow()
    if (!win) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Detect Ctrl+K (Windows/Linux) or Cmd+K (Mac)
      const isModifierKey = event.ctrlKey || event.metaKey
      const isKKey = event.key === 'k' || event.key === 'K'

      if (isModifierKey && isKKey) {
        event.preventDefault()
        // useSessionStorage's setter takes a value (no functional updater), so
        // read the latest isOpen from the deps below rather than a stale closure.
        setIsOpen(!isOpen)
      }
    }

    win.document.addEventListener('keydown', handleKeyDown)
    return () => win.document.removeEventListener('keydown', handleKeyDown)
  }, [enableKeyboardShortcut, isOpen, setIsOpen])

  const toggleOpen = () => setIsOpen(!isOpen)
  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  const toggleMaximized = () => setIsMaximized(!isMaximized)
  const setMaximized = (value: boolean) => setIsMaximized(value)

  const value: FloatingChatBubbleContextType = {
    isOpen,
    toggleOpen,
    open,
    close,
    isMaximized,
    toggleMaximized,
    setMaximized,
  }

  return <CoreProvider value={value}>{children}</CoreProvider>
}

/**
 * Hook to access the floating chat bubble context.
 *
 * Must be used within a FloatingChatBubbleProvider.
 *
 * @returns Context value with isOpen state and toggle functions
 *
 * @example
 * ```tsx
 * const { isOpen, toggleOpen, open, close } = useFloatingChatBubbleContext()
 * ```
 */
export const useFloatingChatBubbleContext = () => useContextCore(context)
